import React, { memo, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styles from './styles.module.scss'
import { usePartyCount } from './usePartyCount.hook'
import ProgressBar from '../ProgressBar'

const PartyCountPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const history = useHistory()
  const { partyCount, error, handlePartyCountChange, handleSubmit, handleSkip, formState } = usePartyCount()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validatePartyCount = (value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter the number of people in your wedding party'
    }

    const numValue = Number(value)
    if (isNaN(numValue)) {
      return 'Please enter a valid number'
    }

    if (!Number.isInteger(numValue)) {
      return 'Please enter a whole number'
    }

    if (numValue < 0) {
      return 'Wedding party count must be greater than or equal to 0'
    }

    return null
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSubmit()
  }

  const handleNextClick = () => {
    if (partyCount.trim()) {
      handleSubmit()
    }
  }

  return (
    <>
      <ProgressBar />
      <div className={styles.partyCountPage}>
        <div className={styles.partyCountLeftSection}>
          <div className={styles.partyCountTextContainer}>
            <span className={styles.partyCountTitle}>How many people are in your wedding party?</span>
          </div>
        </div>

        <div className={styles.partyCountRightSection}>
          <form onSubmit={handleFormSubmit} className={styles.partyCountForm}>
            <div className={`${styles.partyCountInputContainer} ${error ? styles.hasError : ''}`}>
              <input
                className={styles.partyCountInput}
                placeholder="Type your wedding party count here"
                value={partyCount}
                onChange={handlePartyCountChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleFormSubmit(e)
                  }
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-label="Wedding party count"
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? 'party-count-error' : undefined}
                ref={inputRef}
              />
              {error && (
                <div id="party-count-error" className={styles.errorMessage} role="alert" aria-live="polite">
                  {error}
                </div>
              )}
            </div>
          </form>
        </div>
        <span className={styles.skipTextMobile}>
          Not sure?{' '}
          <a onClick={handleSkip} className={styles.skipLink}>
            Skip this question.
          </a>
        </span>
        <button
          className={`${styles.partyCountButtonMobile} ${partyCount.trim() ? styles.hasValue : ''}`}
          onClick={handleNextClick}
          disabled={!partyCount.trim() || formState.isSubmitting}
        >
          Next
        </button>
      </div>

      <div className={styles.continueDesktopContainer}>
        <div className={styles.continueDesktopWrapper}>
          <span className={styles.skipText}>
            Not sure?{' '}
            <a onClick={handleSkip} className={styles.skipLink}>
              Skip this question.
            </a>
          </span>
          <button
            className={`${styles.continueButton} ${partyCount.trim() ? styles.hasValue : ''}`}
            onClick={handleNextClick}
            disabled={!partyCount.trim() || formState.isSubmitting}
          >
            Next
          </button>
        </div>
      </div>
    </>
  )
}

PartyCountPage.displayName = 'PartyCountPage'

export default memo(PartyCountPage)
